import { RemoveCircle, RemoveRedEye } from '@mui/icons-material';
import { Button, FormControl, FormHelperText, FormLabel, IconButton, Popover } from '@mui/material';
import { Color, ColorPicker, ColorValue, createColor } from 'mui-color';
import { useCallback, useMemo, useState } from 'react';
import { Form } from '~/shared/form';
import { Box, Row } from '../view.box';

export const FieldColor = Form.field<string, { label?: string; defaultColor?: string; readOnly?: boolean }>(
	({ field, label, readOnly, defaultColor = '#000000', ...props }) => {
		const color = useMemo(() => createColor(field.value || defaultColor), [defaultColor, field.value]);

		const handleChange = useCallback((value: ColorValue) => field.set(getColorHex(value)), [field]);

		return (
			<FormControl fullWidth>
				<FormLabel sx={{ fontSize: 12 }}>{label || field.label}</FormLabel>
				<ColorPicker value={color} onChange={handleChange} />
				<FormHelperText error={Boolean(field.error)}>{field.error}</FormHelperText>
			</FormControl>
		);
	},
);

export const FieldColorArray = Form.field<
	string[],
	{
		label?: string;
		defaultColor?: string;
		readOnly?: boolean;
		maxColors?: number;
		renderPreview?: (color: string) => React.ReactNode;
	}
>(({ field, label, readOnly, defaultColor = '#ffffff', maxColors = 2, renderPreview, ...props }) => {
	const colors = useMemo(() => field.value?.map(color => createColor(color)) || [], [field.value]);

	const setColorValues = useCallback(
		(cb: (values: ColorValue[]) => ColorValue[]) => {
			const newColors = cb(Array.from(colors));
			const colorStrings = newColors.map(getColorHex);
			field.set(colorStrings);
		},
		[colors, field],
	);

	const handleChange = useCallback(
		(index: number, color: ColorValue) => {
			setColorValues(colors => {
				colors[index] = color;
				return colors;
			});
		},
		[setColorValues],
	);

	const addColor = useCallback(() => {
		setColorValues(colors => colors.concat(createColor(defaultColor)));
	}, [setColorValues, defaultColor]);

	const removeColor = useCallback(
		(index: number) => setColorValues(colors => colors.filter((_, i) => i !== index)),
		[setColorValues],
	);

	const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [previewColor, setPreviewColor] = useState<Color | null>(null);
	const hadnlePreviewOpen = useCallback((target: HTMLButtonElement, color: Color) => {
		setPreviewColor(color);
		setPreviewAnchorEl(target);
	}, []);

	return (
		<FormControl fullWidth>
			<Row gap={2} alignItems="center" justifyContent="space-between">
				<FormLabel sx={{ fontSize: 12 }}>{label || field.label}</FormLabel>
				<Button size="small" onClick={addColor} sx={{ mt: 2 }} disabled={colors.length >= maxColors}>
					Add Color
				</Button>
			</Row>
			<Row gap={1} justifyContent="flex-start" flexWrap="wrap" maxWidth={900}>
				{colors.map((color, index) => (
					<Row key={index} justifyContent="flex-start" maxWidth={290}>
						<ColorPicker value={color} onChange={newColor => handleChange(index, newColor)} />
						{renderPreview && (
							<IconButton size="small" onClick={e => hadnlePreviewOpen(e.currentTarget, color)}>
								<RemoveRedEye />
							</IconButton>
						)}
						<IconButton size="small" onClick={() => removeColor(index)}>
							<RemoveCircle />
						</IconButton>
					</Row>
				))}
			</Row>
			<FormHelperText error={Boolean(field.error)}>{field.error}</FormHelperText>
			{renderPreview && previewColor && (
				<Popover
					open={Boolean(previewAnchorEl)}
					anchorEl={previewAnchorEl}
					onClose={() => setPreviewAnchorEl(null)}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					<Box width={250} height={250} p={0.5}>
						{renderPreview(previewColor.raw.toString())}
					</Box>
				</Popover>
			)}
		</FormControl>
	);
});

const getColorHex = (color: ColorValue): string => {
	return typeof color === 'object' && 'hex' in color ? `#${color.hex}` : color.toString();
};
